import { useMemo, useState } from 'react';
import { useMemoizedFn, useSetState } from 'ahooks';
import { isEmpty } from 'lodash-es';
import {
  sortable,
  filterable,
  mergeable,
} from 'tea-component/lib/table/addons';

// 常量定义
const DEFAULT_CONFIG = {
  // 单列过滤选项默认配置
  COLUMN_FILTER: {
    type: 'single' as const,
    value: '',
    all: {
      value: 'ALL',
      text: 'ALL',
    },
    options: [],
  },
  // 分页默认值
  PAGING: {
    pageIndex: 1,
    pageSize: 10,
  },
} as const;

// 定义排序初始值类型
export type TSortDefaultValue = {
  //  排序列
  by: string;
  //  排序顺序
  order: 'asc' | 'desc';
  //  排序函数
  sorter?: undefined;
};
// 定义过滤列配置类型
export type TFilterColumn = {
  // 过滤列
  key: string;
  // 过滤选项
  options: Array<{ value: string; text: string; }>;
  // 过滤值
  value: string;
  // 支持透传props
  [key: string]: any;
};
// 定义分页变化回调类型
type TPagingChange = (params: { pageIndex: number; pageSize: number; }) => void;

// 定义组件props类型
type TTableConfigProps = {
  // 排序列配置
  sortColumn: string[];
  // 排序初始值
  sortDefaultValue: TSortDefaultValue[];
  // 过滤列配置
  filterColumn: TFilterColumn[];
  // 分页变化回调
  handleOnPagingChange: TPagingChange;
  // 表格列配置合并
  getRowGroupNumList?: () => void;
  // 是否单双列排序、默认单列排序
  singleSort?: boolean;
};

/** @example
  *
  * const { sortCfg, filterCfg, tableTypeCfg, sort, filters, selectedKeys, handleOnSortChange } = useTableConfigManager({
    handleOnPagingChange,
    sortColumn: [ 'update_time', 'path' ],
    sortDefaultValue: [
      {
        order: 'desc',
        by: 'update_time',
      }
    ],
    filterColumn: [
      {
        key: 'business',
        options: [],
        value: 'ALL',
        searchable: true,
      },
      {
        key: 'data_frame',
        options: [],
        value: 'ALL',
      },
    ],
    rowGroupNumList: [1, 2, 3],
  });
  */

// 定制化表格排序、过滤字段配置解析
export const useTableConfigManager = (props: Partial<TTableConfigProps>) => {
  const {
    sortColumn,
    filterColumn,
    sortDefaultValue,
    handleOnPagingChange,
    singleSort = true,
  } = props || {};
  const [sort, setSorts] = useState<TSortDefaultValue[] | undefined>(
    () => sortDefaultValue || [],
  );

  // 生成初始filters
  const getInitialFilters = useMemoizedFn(() => {
    if (!filterColumn) return {};
    return filterColumn.reduce((acc, item) => {
      if (item.value) {
        acc[item.key] = item.value;
      }
      return acc;
    }, {} as any);
  });

  const [filters, setFilters] = useSetState(getInitialFilters);
  // 排序点击处理函数
  const handleOnSortChange = useMemoizedFn((value: any) => {
    setSorts(() => {
      if (singleSort) {
        // 单列排序时直接返回新值，忽略之前的状态
        return (
          value?.[0]?.order &&
          value?.[0]?.by && [
            {
              order: value?.[0]?.order,
              by: value?.[0]?.by,
            },
          ]
        );
      }
      // 多列排序时使用新值
      return value;
    });
  });

  // 处理表格类型配置
  const {
    sortCfg,
    filterCfg,
    // , mergeRowCfg
  } = useMemo(() => {
    // 处理排序配置
    const sortConfig = !isEmpty(sortColumn)
      ? sortable({
        columns: sortColumn as any,
        value: sort,
        onChange: handleOnSortChange,
      })
      : undefined;
    // 处理过滤配置
    const filterConfig = !isEmpty(filterColumn)
      ? filterColumn?.map((item) =>
        filterable({
          ...DEFAULT_CONFIG.COLUMN_FILTER,
          ...item,
          column: item.key,
          value: filters[item.key] || item.value,
          onChange: (value: string) => {
            handleOnPagingChange?.(DEFAULT_CONFIG.PAGING);
            setFilters({ [item.key]: value });
          },
        }),
      )
      : undefined;

    return {
      sortCfg: sortConfig,
      filterCfg: filterConfig,
      // mergeRowCfg: mergeRow,
    };
  }, [
    sortColumn,
    sort,
    handleOnSortChange,
    filterColumn,
    filters,
    handleOnPagingChange,
    setFilters,
  ]);

  // 重置所有状态到初始值
  const handleResetAll = useMemoizedFn(() => {
    setSorts(sortDefaultValue || []);
    setFilters(getInitialFilters());
  });

  return {
    sortCfg,
    filterCfg,
    // mergeRowCfg,
    sort,
    filters,
    handleOnSortChange,
    handleResetAll,
  };
};
